import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  Paper,
  Stack,
  SelectChangeEvent,
  ToggleButton,
  ToggleButtonGroup
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  AutoAwesome as AutoAwesomeIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
  Group as GroupIcon,
  FileDownload as FileDownloadIcon,
  UploadFile as UploadFileIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
} from "@mui/icons-material";
import { API_BASE, getAuthToken, removeAuthToken } from "../../api/agentApi";
import { useNavigate } from "react-router-dom";
import { useFeatureFlags } from "../../context/FeatureFlagContext";
import { useUser } from "../UserContext.jsx";
import { BRAND_COLORS, PAGE_SHELL, PAGE_TYPOGRAPHY, UI_RADIUS } from "../../constants";
import { PERMISSIONS } from "../../permissions";
import {
  CATEGORY_OPTIONS,
  getRequirementLabel,
  getToolCatalogMetadata,
  groupToolsByCollection,
  type CanonicalCategory,
  type SetupRequirement,
} from "../../toolCatalogMetadata";

// Types based on the OpenAPI schema
interface Tool {
  id: string;
  name: string;
  display_name: string;
  version: number; // Integer version (1, 2, 3, etc.)
  description: string;
  display_description: string;
  category: string | null;
  tags: string[];
  provider?: string | null;
  provider_label?: string | null;
  auth_required?: boolean | null;
  keys_schema?: any[];
  canonical_category?: CanonicalCategory | null;
  collection?: string | null;
  capability_tags?: string[];
  setup_requirements?: SetupRequirement[];
  // code field removed - stored on disk
  // dependencies field removed
  parameters: Record<string, any>;
  returns: Record<string, any> | null;
  invocation_type: string;
  invocation_config: Record<string, any> | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  is_public: boolean;
  approved_by: string | null;
  approved_at: string | null;
  status: "draft" | "published" | "deprecated"; // 3 lifecycle statuses
  deprecation_reason: string | null;
  successor_version: number | null; // Integer version, not UUID
  usage_count: number;
  exportable?: boolean;
}

const CATEGORIES = [
  { value: "all", label: "All Categories" },
  ...CATEGORY_OPTIONS.map((category) => ({ value: category.id, label: category.label })),
];

const COPPER = BRAND_COLORS.primary.main;
const COPPER_LIGHT = BRAND_COLORS.primary.light;
const COPPER_DARK = BRAND_COLORS.primary.dark;
const BORDER_SOFT = "#E5D4C4";
const CATALOG_GREEN = "#4caf50";
const CATALOG_GREEN_LIGHT = "#66bb6a";
const CATALOG_GREEN_SOFT = "rgba(76, 175, 80, 0.12)";

const formControlSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: UI_RADIUS.input,
  },
};

const primaryActionButtonSx = {
  borderRadius: UI_RADIUS.button,
  bgcolor: "primary.main",
  boxShadow: `0 10px 24px ${BRAND_COLORS.primary.shadow}`,
  color: "#fff",
  textTransform: "none",
  fontWeight: 900,
  minHeight: 44,
  minWidth: { sm: 160 },
  px: 2.4,
  width: { xs: "100%", sm: "auto" },
  "&:hover": {
    bgcolor: "primary.dark",
    boxShadow: `0 14px 30px ${BRAND_COLORS.primary.shadowHover}`,
  },
};

const secondaryActionButtonSx = {
  borderRadius: UI_RADIUS.button,
  borderColor: BORDER_SOFT,
  color: COPPER_DARK,
  textTransform: "none",
  fontWeight: 850,
  minHeight: 44,
  px: 2.2,
  width: { xs: "100%", sm: "auto" },
  "&:hover": {
    borderColor: COPPER,
    bgcolor: BRAND_COLORS.primary.softSubtle,
  },
};

const redirectToLoginOnUnauthorized = (response: Response) => {
  if (response.status !== 401) {
    return false;
  }
  removeAuthToken();
  window.location.replace("/login");
  return true;
};

const ToolMarketplace: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const pageSurface = isDark ? "#181411" : "#fffdfb";
  const pageSurfaceElevated = isDark ? "#211B16" : "#ffffff";
  const softSurface = isDark ? "rgba(255,247,239,0.08)" : "#f5eee8";
  const borderSoft = isDark ? "rgba(224,177,132,0.24)" : "#ead9c9";
  const copperText = isDark ? "#F2C69B" : "#8f5726";
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedCollection, setSelectedCollection] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [visibilityFilter, setVisibilityFilter] = useState("all");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [tools, setTools] = useState<Tool[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [exportingToolId, setExportingToolId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();
  const { canAny } = useUser();
  const { flags } = useFeatureFlags();
  const canUseCreateTool = Boolean(
    flags.enableCreateTool && canAny([PERMISSIONS.TOOLS_CREATE, PERMISSIONS.TOOLS_CONFIGURE])
  );

  const isCreateToolGenerated = (tool: Tool): boolean => {
    return Boolean(tool.exportable);
  };

  // Fetch tools from API
  const fetchTools = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();

      if (selectedStatus !== "all") {
        params.append("status", selectedStatus);
      }
      if (visibilityFilter === "public") {
        params.append("is_public", "true");
      } else if (visibilityFilter === "private") {
        params.append("is_public", "false");
      }

      const token = getAuthToken();
      const response = await fetch(`${API_BASE}/tools?${params.toString()}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (redirectToLoginOnUnauthorized(response)) {
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch tools: ${response.statusText}`);
      }

      const data = await response.json();
      setTools(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatus, visibilityFilter]);

  useEffect(() => {
    setSelectedCollection(null);
  }, [selectedCategory, searchQuery]);

  // Filter tools by search query
  const filteredBySearchAndCategory = tools.filter((tool: Tool) => {
    const query = searchQuery.toLowerCase();
    const metadata = getToolCatalogMetadata(tool);
    const textMatches =
      tool.name.toLowerCase().includes(query) ||
      (tool.display_name || "").toLowerCase().includes(query) ||
      (tool.display_description || tool.description || "").toLowerCase().includes(query) ||
      metadata.collection.toLowerCase().includes(query) ||
      metadata.canonicalCategory.label.toLowerCase().includes(query) ||
      (tool.tags || []).some((tag: string) => tag.toLowerCase().includes(query));
    const categoryMatches =
      selectedCategory === "all" || metadata.canonicalCategory.id === selectedCategory;
    return textMatches && categoryMatches;
  });
  const collectionGroups = groupToolsByCollection(filteredBySearchAndCategory);
  const filteredTools = selectedCollection
    ? filteredBySearchAndCategory.filter((tool) => getToolCatalogMetadata(tool).collection === selectedCollection)
    : filteredBySearchAndCategory;

  // Group tools by category for stats
  const toolStats = {
    total: tools.length,
    byCategory: CATEGORIES.reduce((acc, cat) => {
      if (cat.value === "all") return acc;
      acc[cat.value] = tools.filter(
        (t: Tool) => getToolCatalogMetadata(t).canonicalCategory.id === cat.value
      ).length;
      return acc;
    }, {} as Record<string, number>),
    byStatus: {
      draft: tools.filter((t: Tool) => t.status === "draft").length,
      published: tools.filter((t: Tool) => t.status === "published").length,
      deprecated: tools.filter((t: Tool) => t.status === "deprecated").length,
    },
  };

  const handleViewTool = (toolId: string) => {
    navigate(`/tool/${toolId}`);
  };

  const handleImportBundleClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportBundle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }

    setIsImporting(true);
    setError(null);
    setNotice(null);

    try {
      const token = getAuthToken();
      const formData = new FormData();
      formData.append("bundle", file);
      formData.append("publish", "true");
      formData.append("is_public", "true");
      formData.append("require_validation", "false");
      formData.append("require_sandbox", "false");
      formData.append("index", "true");

      const response = await fetch(`${API_BASE}/tools/bundles/import`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (redirectToLoginOnUnauthorized(response)) {
        return;
      }

      if (!response.ok) {
        const detail = await response.json().catch(() => null);
        throw new Error(detail?.detail || `Failed to import bundle: ${response.statusText}`);
      }

      const result = await response.json();
      setNotice(`Imported ${result.tool?.display_name || result.tool?.name || "tool bundle"} into the catalog.`);
      await fetchTools();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown import error occurred");
    } finally {
      setIsImporting(false);
    }
  };

  const handleExportBundle = async (tool: Tool) => {
    if (!isCreateToolGenerated(tool)) {
      setError("Only tools generated with Tool Forge can be exported.");
      return;
    }

    setExportingToolId(`${tool.id}:${tool.version}`);
    setError(null);
    setNotice(null);

    try {
      const token = getAuthToken();
      const response = await fetch(
        `${API_BASE}/tools/${tool.id}/version/${tool.version}/bundle/export`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (redirectToLoginOnUnauthorized(response)) {
        return;
      }

      if (!response.ok) {
        const detail = await response.json().catch(() => null);
        throw new Error(detail?.detail || `Failed to export bundle: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const filename = `${tool.name}-${tool.id}-v${tool.version}.zip`;
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setNotice(`Exported ${tool.display_name || tool.name} v${tool.version}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown export error occurred");
    } finally {
      setExportingToolId(null);
    }
  };

  const generateSummaryText = () => {
    if (selectedCollection) {
      return `${filteredTools.length} ${filteredTools.length === 1 ? "tool" : "tools"} in ${selectedCollection}`;
    }
    return `${collectionGroups.length} ${collectionGroups.length === 1 ? "collection" : "collections"} across ${filteredBySearchAndCategory.length} tools`;
  };

  return (
    <Box sx={{ px: PAGE_SHELL.paddingX, py: PAGE_SHELL.paddingY, maxWidth: PAGE_SHELL.maxWidth, mx: "auto", color: "text.primary" }}>
      <Stack spacing={1.9} sx={{ mb: PAGE_SHELL.headerMarginBottom }}>
        <Box>
          <Typography variant="h3" component="h1" sx={PAGE_TYPOGRAPHY.heading}>
            Tool Catalog
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.35 }}>
            Browse and discover reusable tools for your agents.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap" sx={{ pt: 0.2 }}>
          {canUseCreateTool && (
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip,application/zip"
              hidden
              onChange={handleImportBundle}
            />
          )}
          {canUseCreateTool && (
            <Button
              variant="contained"
              startIcon={<AutoAwesomeIcon />}
              onClick={() => navigate("/tool-forge")}
              sx={primaryActionButtonSx}
            >
              Create Tool
            </Button>
          )}
          {canUseCreateTool && (
            <Button
              variant="outlined"
              startIcon={isImporting ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : <UploadFileIcon />}
              onClick={handleImportBundleClick}
              disabled={isImporting}
              sx={secondaryActionButtonSx}
            >
              Import Bundle
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchTools}
            disabled={isLoading}
            sx={secondaryActionButtonSx}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: PAGE_SHELL.sectionGap,
          borderRadius: UI_RADIUS.panel,
          border: `1px solid ${borderSoft}`,
          bgcolor: pageSurfaceElevated,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
          }}
        >
          {/* Search */}
          <Box sx={{ flexGrow: 1, minWidth: { xs: "100%", sm: 300 } }}>
            <TextField
              fullWidth
              size="small"
              sx={formControlSx}
              placeholder="Search tools by name, description, or tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Category Filter */}
          <Box sx={{ minWidth: { xs: "100%", sm: 200 } }}>
            <FormControl fullWidth size="small" sx={formControlSx}>
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategory}
                label="Category"
                onChange={(e: SelectChangeEvent) =>
                  setSelectedCategory(e.target.value)
                }
              >
                {CATEGORIES.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <span>{cat.label}</span>
                      {cat.value !== "all" && (
                        <Chip
                          label={toolStats.byCategory[cat.value] || 0}
                          size="small"
                          sx={{ ml: "auto" }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Status Filter */}
          <Box sx={{ minWidth: { xs: "100%", sm: 150 } }}>
            <FormControl fullWidth size="small" sx={formControlSx}>
              <InputLabel>Status</InputLabel>
              <Select
                value={selectedStatus}
                label="Status"
                onChange={(e: SelectChangeEvent) =>
                  setSelectedStatus(e.target.value)
                }
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="published">Published</MenuItem>
                <MenuItem value="deprecated">Deprecated</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Visibility Filter */}
          <Box sx={{ minWidth: { xs: "100%", sm: 150 } }}>
            <FormControl fullWidth size="small" sx={formControlSx}>
              <InputLabel>Visibility</InputLabel>
              <Select
                value={visibilityFilter}
                label="Visibility"
                onChange={(e: SelectChangeEvent) =>
                  setVisibilityFilter(e.target.value)
                }
              >
                <MenuItem value="all">All Tools</MenuItem>
                <MenuItem value="public">Public Only</MenuItem>
                <MenuItem value="private">Private Only</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0, ml: { xs: 0, sm: "auto" } }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={viewMode}
              onChange={(_, value) => value && setViewMode(value)}
              aria-label="Tool catalog view"
              sx={{ "& .MuiToggleButton-root": { borderRadius: UI_RADIUS.button } }}
            >
              <ToggleButton value="grid" aria-label="Grid view">
                <ViewModuleIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="list" aria-label="List view">
                <ViewListIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      </Paper>

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {notice && (
        <Alert severity="success" sx={{ mb: 4 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      {/* Empty State */}
      {!isLoading && filteredBySearchAndCategory.length === 0 && (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No tools found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your filters or search query
          </Typography>
        </Box>
      )}

      {/* Tools Grid */}
      {!isLoading && filteredBySearchAndCategory.length > 0 && (
        <>
          {/* Display Results Summary with Stats */}
          <Stack direction="row" spacing={2} sx={{ mb: 3, alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterListIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                {generateSummaryText()}
              </Typography>
            </Box>
            {selectedCollection && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => setSelectedCollection(null)}
                sx={{
                  borderRadius: UI_RADIUS.button,
                  textTransform: "none",
                  borderColor: isDark ? "rgba(224,177,132,0.34)" : "#d7b99e",
                  color: copperText,
                  fontWeight: 700,
                }}
              >
                All collections
              </Button>
            )}
          </Stack>

          {!selectedCollection ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns:
                  viewMode === "list"
                    ? "1fr"
                    : {
                        xs: "1fr",
                        sm: "repeat(2, 1fr)",
                        md: "repeat(3, 1fr)",
                      },
                gap: viewMode === "list" ? 1.5 : 3,
              }}
            >
              {collectionGroups.map((group) => {
                const primaryCategory = group.categories[0];
                const readyCount = group.tools.filter((tool) => tool.status === "published").length;
                const setupCount = group.tools.reduce(
                  (count, tool) =>
                    count +
                    getToolCatalogMetadata(tool).setupRequirements.filter(
                      (requirement) => requirement.required !== false && !requirement.satisfied
                    ).length,
                  0
                );
                return (
                  <Card
                    key={group.name}
                    onClick={() => setSelectedCollection(group.name)}
                    sx={{
                      cursor: "pointer",
                      minHeight: viewMode === "list" ? 0 : 190,
                      border: "1px solid",
                      borderColor: borderSoft,
                      borderRadius: "18px",
                      bgcolor: pageSurface,
                      color: "text.primary",
                      boxShadow: isDark ? "0 14px 34px rgba(0,0,0,0.30)" : "0 8px 22px rgba(31, 24, 18, 0.06)",
                      transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: isDark ? "0 18px 42px rgba(0,0,0,0.42)" : "0 16px 36px rgba(31, 24, 18, 0.12)",
                        borderColor: "#b87537",
                      },
                    }}
                  >
                    <CardContent sx={{ p: viewMode === "list" ? 2 : 3 }}>
                      <Stack direction={viewMode === "list" ? "row" : "column"} alignItems={viewMode === "list" ? "center" : "stretch"} justifyContent="space-between" gap={2}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="h6" noWrap={viewMode === "list"} sx={{ fontWeight: 850, letterSpacing: 0, mb: 0.6 }}>
                            {group.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: viewMode === "list" ? 1 : 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                            {primaryCategory?.description || "Reusable tools grouped by setup and capability."}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={viewMode === "list" ? "flex-end" : "flex-start"} sx={{ flexShrink: 0 }}>
                          {readyCount > 0 && (
                            <Chip
                              size="small"
                              label={`${readyCount} ready`}
                              sx={{
                                borderRadius: UI_RADIUS.chip,
                                bgcolor: isDark ? "rgba(145,205,153,0.18)" : CATALOG_GREEN_SOFT,
                                color: isDark ? "#CFEFD4" : CATALOG_GREEN,
                                fontWeight: 800,
                              }}
                            />
                          )}
                          {setupCount > 0 && (
                            <Chip
                              size="small"
                              label={`${setupCount} setup needed`}
                              sx={{
                                borderRadius: UI_RADIUS.chip,
                                bgcolor: softSurface,
                                color: copperText,
                                fontWeight: 800,
                              }}
                            />
                          )}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns:
                  viewMode === "list"
                    ? "1fr"
                    : {
                        xs: "1fr",
                        sm: "repeat(2, 1fr)",
                        md: "repeat(3, 1fr)",
                      },
                gap: viewMode === "list" ? 1.5 : 5,
              }}
            >
            {filteredTools.map((tool: Tool) => {
              const metadata = getToolCatalogMetadata(tool);
              const setupRequirements = metadata.setupRequirements;
              const generatedTool = isCreateToolGenerated(tool);
              const isList = viewMode === "list";
              const cardAccent = generatedTool
                ? `linear-gradient(90deg, ${COPPER_LIGHT}, ${COPPER})`
                : `linear-gradient(90deg, ${CATALOG_GREEN}, ${CATALOG_GREEN_LIGHT})`;

              return (
              <Card
                key={`${tool.id}:${tool.version}`}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: isList ? 'row' : 'column',
                  alignItems: isList ? 'center' : 'stretch',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '16px',
                  boxShadow: isDark ? '0 12px 30px rgba(0,0,0,0.32)' : '0 2px 8px rgba(0,0,0,0.08)',
                  backgroundColor: pageSurfaceElevated,
                  color: 'text.primary',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: cardAccent,
                    opacity: 0.8,
                  },
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: isDark ? '0 16px 42px rgba(0,0,0,0.46)' : '0 12px 28px rgba(0,0,0,0.15)',
                    borderColor: generatedTool ? COPPER : CATALOG_GREEN,
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: isList ? 2 : 3, pt: isList ? 2 : 2.5, minWidth: 0 }}>
                  {/* Header with Title and Status */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography
                      variant="h6"
                      component="div"
                      noWrap={isList}
                      sx={{
                        fontWeight: 700,
                        fontSize: '1.1rem',
                        color: 'text.primary',
                        letterSpacing: '-0.01em',
                        mb: 0.75,
                      }}
                    >
                      {tool.display_name}
                    </Typography>
                  </Box>

                  {/* Description */}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 1.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      fontSize: '0.875rem',
                      lineHeight: 1.4,
                      minHeight: '2.5em',
                    }}
                  >
                    {tool?.display_description}
                  </Typography>

                  {/* Category and setup badges */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    flexWrap: "wrap",
                    mb: 1.5,
                  }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontWeight: 600,
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                      }}
                    >
                      {metadata.canonicalCategory.label}
                    </Typography>
                    {metadata.providerLabel && (
                      <Chip
                        label={metadata.providerLabel}
                        size="small"
                        variant="outlined"
                          sx={{ height: 22, fontSize: "0.7rem", borderColor: isDark ? "rgba(224,177,132,0.28)" : "#e2cab5" }}
                      />
                    )}
                  </Box>

                  {setupRequirements.length > 0 && (
                    <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                      {setupRequirements.slice(0, 2).map((requirement) => (
                        <Chip
                          key={requirement.id}
                          label={getRequirementLabel(requirement)}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: "0.68rem",
                            bgcolor: requirement.satisfied
                              ? (isDark ? "rgba(91,160,92,0.18)" : "#eef7ee")
                              : (isDark ? "rgba(224,177,132,0.14)" : "#fff4e8"),
                            color: requirement.satisfied ? (isDark ? "#CFEFD4" : "#2f6f35") : copperText,
                            border: "1px solid",
                            borderColor: requirement.satisfied
                              ? (isDark ? "rgba(145,205,153,0.30)" : "#cce6cf")
                              : (isDark ? "rgba(224,177,132,0.28)" : "#efd0af"),
                            fontWeight: 700,
                          }}
                        />
                      ))}
                      {setupRequirements.length > 2 && (
                        <Chip
                          label={`+${setupRequirements.length - 2}`}
                          size="small"
                          sx={{ height: 22, fontSize: "0.68rem", bgcolor: softSurface, color: "text.secondary", fontWeight: 700 }}
                        />
                      )}
                    </Stack>
                  )}

                  {/* Tags */}
                  {tool.tags && tool.tags.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                      {tool.tags.slice(0, 3).map((tag: string) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontSize: '0.7rem',
                            height: '22px',
                            borderRadius: '6px',
                          }}
                        />
                      ))}
                      {tool.tags.length > 3 && (
                        <Chip
                          label={`+${tool.tags.length - 3} more`}
                          size="small"
                          variant="filled"
                          sx={{
                            fontSize: '0.7rem',
                            height: '22px',
                            bgcolor: 'action.selected',
                            fontWeight: 600,
                          }}
                        />
                      )}
                    </Box>
                  )}

                  {/* Usage Count */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 0.5,
                    pt: 1,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  }}>
                    <GroupIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      Used by <strong style={{ color: 'primary.main' }}>{tool.usage_count}</strong>{' '}
                      {tool.usage_count === 1 ? 'agent' : 'agents'}
                    </Typography>
                  </Box>
                </CardContent>

                <Divider />

                {/* Footer Actions */}
                <CardActions sx={{ 
                  px: 3, 
                  py: 2, 
                  justifyContent: 'space-between',
                  gap: 1.25,
                  flexWrap: 'nowrap',
                  bgcolor: 'action.hover',
                }}>
                  <Button
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleViewTool(tool.id)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      '&:hover': {
                        bgcolor: 'transparent',
                        color: 'primary.dark',
                      },
                    }}
                  >
                    View Details
                  </Button>
                  {canUseCreateTool && isCreateToolGenerated(tool) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                      <Button
                        size="small"
                        startIcon={
                          exportingToolId === `${tool.id}:${tool.version}` ? (
                            <CircularProgress size={14} />
                          ) : (
                            <FileDownloadIcon />
                          )
                        }
                        onClick={() => handleExportBundle(tool)}
                        disabled={exportingToolId === `${tool.id}:${tool.version}`}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          color: copperText,
                          '&:hover': {
                            bgcolor: 'rgba(184, 117, 55, 0.08)',
                          },
                        }}
                      >
                        Export
                      </Button>
                    </Box>
                  )}
                </CardActions>
              </Card>
              );
            })}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ToolMarketplace;
